import React, { useState, useEffect, useCallback } from 'react'
import { inject, observer } from 'mobx-react'
import { Popover, Avatar, Spin } from 'antd'
import { apiUrl } from '../../../config'
import { UserRequest } from '../../../requests/UserRequest'
import { colors } from '../NewsFeedStyled'

const formatName = name => {
  if (!name) return ''
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const resolveAvatarUrl = url => {
  if (!url) return null
  if (url.startsWith('http')) return url
  return `${apiUrl}${url}`
}

const UserProfilePopover = inject(
  'positionStore',
  'departmentStore',
  'authenticationStore',
  'userStore'
)(
  observer(
    ({
      userCode,
      displayName,
      avatarUrl,
      children,
      positionStore,
      departmentStore,
      authenticationStore,
      userStore,
    }) => {
      const [visible, setVisible] = useState(false)
      const [detail, setDetail] = useState(null)
      const [loading, setLoading] = useState(false)
      const [fetched, setFetched] = useState(false)

      useEffect(() => {
        setDetail(null)
        setFetched(false)
      }, [userCode])

      useEffect(() => {
        if (visible && !fetched && !loading && userCode) {
          setLoading(true)

          // Load stores lists if they are empty
          if (
            positionStore &&
            (!positionStore.positionList ||
              positionStore.positionList.length === 0)
          ) {
            positionStore.getPositionList()
          }
          if (
            departmentStore &&
            (!departmentStore.departmentList ||
              departmentStore.departmentList.length === 0)
          ) {
            departmentStore.getDepartmentList()
          }

          const companyCode =
            authenticationStore?.currentUser?.company?.code ||
            authenticationStore?.currentUser?.company_code

          UserRequest.getUserList(
            5,
            0,
            userCode,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            companyCode
          )
            .then(res => {
              const list = res?.data?.data || []
              const found = list.find(
                u =>
                  u.username?.toLowerCase() === userCode?.toLowerCase() ||
                  u.code?.toLowerCase() === userCode?.toLowerCase()
              )
              if (found) {
                setDetail(found)
              } else if (list.length > 0) {
                setDetail(list[0])
              }
              setFetched(true)
            })
            .catch(err => {
              console.error('Failed to load user detail', err)
              setFetched(true)
            })
            .finally(() => {
              setLoading(false)
            })
        }
      }, [
        visible,
        userCode,
        fetched,
        loading,
        positionStore,
        departmentStore,
        authenticationStore,
      ])

      const convertCodeToPositionName = useCallback(
        input => {
          if (!input) return ''
          const positionList = positionStore?.positionList || []
          const codes = input.split(',').map(code => code.trim())
          const names = codes.map(code => {
            const role = positionList.find(r => r.code === code)
            return role ? role.name : code
          })
          return names.join(', ')
        },
        [positionStore?.positionList]
      )

      const convertCodeToDeptName = useCallback(
        input => {
          if (!input) return ''
          const departmentList = departmentStore?.departmentList || []
          const codes = input.split(',').map(code => code.trim())
          const names = codes.map(code => {
            const dept = departmentList.find(d => d.code === code)
            return dept ? dept.name : code
          })
          return names.join(', ')
        },
        [departmentStore?.departmentList]
      )

      // Derived fields from details or fallbacks
      const displayVal =
        detail?.name || detail?.name_lowercase
          ? formatName(detail.name || detail.name_lowercase)
          : formatName(displayName || userCode)

      // Find cached user from mention list as initial fallback
      const foundUser = userStore?.mentionUserList?.find(
        u => u.id && u.id.toLowerCase() === userCode?.toLowerCase()
      )

      let resolvedAvatar =
        avatarUrl ||
        (foundUser?.image ? `/api/v1/images/${foundUser.image}` : null)
      if (detail?.imageId) {
        resolvedAvatar = `/api/v1/images/${detail.imageId}`
      }

      const deptName =
        detail?.department?.name ||
        convertCodeToDeptName(detail?.department?.code || detail?.department)
      const posName =
        detail?.position?.name ||
        convertCodeToPositionName(detail?.position?.code || detail?.position)
      const emailVal = detail?.email || ''
      const phoneVal =
        detail?.phone || detail?.homePhone || detail?.home_phone || ''

      const popoverContent = (
        <div style={{ width: 280, padding: '8px 4px' }}>
          <div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                marginBottom: 12,
              }}>
              <Avatar
                size={56}
                src={resolveAvatarUrl(resolvedAvatar)}
                style={{
                  background: colors.primary,
                  color: '#fff',
                  fontSize: 20,
                  fontWeight: 'bold',
                }}>
                {displayVal ? displayVal.charAt(0) : 'U'}
              </Avatar>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: 16,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {displayVal}
                </div>
                {loading ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: '#999',
                      fontStyle: 'italic',
                      marginTop: 4,
                    }}>
                    Đang tải thông tin...
                  </div>
                ) : (
                  posName && (
                    <div
                      style={{
                        fontSize: 13,
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {posName}
                    </div>
                  )
                )}
              </div>
            </div>

            {!loading && (
              <div>
                {deptName && (
                  <div
                    style={{
                      fontSize: 13,
                      marginBottom: 6,
                      display: 'flex',
                      gap: 4,
                    }}>
                    <strong style={{ color: '#555', flexShrink: 0 }}>
                      Đơn vị:
                    </strong>
                    <span style={{ color: '#050505', wordBreak: 'break-word' }}>
                      {deptName}
                    </span>
                  </div>
                )}
                {emailVal && (
                  <div
                    style={{
                      fontSize: 13,
                      marginBottom: 6,
                      display: 'flex',
                      gap: 4,
                      wordBreak: 'break-all',
                    }}>
                    <strong style={{ color: '#555', flexShrink: 0 }}>
                      Email:
                    </strong>
                    <span style={{ color: '#050505' }}>{emailVal}</span>
                  </div>
                )}
                {phoneVal && (
                  <div style={{ fontSize: 13, display: 'flex', gap: 4 }}>
                    <strong style={{ color: '#555', flexShrink: 0 }}>
                      SĐT:
                    </strong>
                    <span style={{ color: '#050505' }}>{phoneVal}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )

      return (
        <Popover
          // content={popoverContent}
          visible={visible}
          onVisibleChange={setVisible}
          trigger="click"
          placement="right">
          {children}
        </Popover>
      )
    }
  )
)

export default UserProfilePopover
